import { useState, useEffect, useRef, useCallback } from "react";

import { DeleteBlockedCode, notifyDeleteBlocked } from "./delete-blocked.jsx";

export function getAuthorizationHeader() {
    return `Bearer ${window.popdockBearerToken}`;
}

export async function data_get(endpoint, signal) {
    try {
        const fetchRequestBody = {
            method: "GET",
            headers: {
                "Accept": "application/json"
            },
            signal: signal
        };
        if (window.popdockBearerToken) {
            fetchRequestBody.headers = {
                "Authorization": `Bearer ${window.popdockBearerToken}`
            };
        }
        const results = await fetch(endpoint, fetchRequestBody);
        return await handleResponse(results);
    } catch (error) {
        if (error.name === "AbortError") return { error: error, aborted: true };

        console.log("get " + endpoint);
        console.log(error);
        return { error: error };
    }
}

export async function data_post(endpoint, body, contentType) {
    try {
        const fetchRequestBody = {
            method: "POST",
            headers: {
                "Content-Type": contentType || "application/json",
                "Accept": "application/json"
            },
            mode: "cors",
            body: JSON.stringify(body)
        };
        if (window.popdockBearerToken) {
            fetchRequestBody.headers.Authorization = `Bearer ${window.popdockBearerToken}`;
        }
        const results = await fetch(endpoint, fetchRequestBody);
        return await handleResponse(results);
    } catch (error) {
        if (error.name === "AbortError") return { error: error, aborted: true };

        console.log("post " + endpoint);
        console.log(error);
        return { error: error };
    }
}

export async function data_patch(endpoint, body, contentType) {
    try {
        const fetchRequestBody = {
            method: "PATCH",
            headers: {
                "Content-Type": contentType || "application/json",
                "Accept": "application/json"
            },
            mode: "cors",
            body: JSON.stringify(body)
        };
        if (window.popdockBearerToken) {
            fetchRequestBody.headers.Authorization = `Bearer ${window.popdockBearerToken}`;
        }
        const results = await fetch(endpoint, fetchRequestBody);
        return await handleResponse(results);
    } catch (error) {
        if (error.name === "AbortError") return { error: error, aborted: true };

        console.log("patch " + endpoint);
        console.log(error);
        return { error: error };
    }
}

export async function data_delete(endpoint) {
    try {
        const fetchRequestBody = {
            method: "DELETE",
            headers: {
                "Accept": "application/json"
            }
        };
        if (window.popdockBearerToken) {
            fetchRequestBody.headers = {
                "Authorization": `Bearer ${window.popdockBearerToken}`
            };
        }
        const results = await fetch(endpoint, fetchRequestBody);
        return await handleResponse(results);
    } catch (error) {
        if (error.name === "AbortError") return { error: error, aborted: true };

        console.log("delete " + endpoint);
        console.log(error);
        return { error: error };
    }
}

export async function handleResponse (response) {
    const resultsText = await response.text();
    if (response.ok) {
        if (resultsText) {
            const resultsJson = JSON.parse(resultsText);
            if (resultsJson && resultsJson.Redirect && window.location.href.replace(window.location.origin, "") !== resultsJson.Redirect) {
                window.location = resultsJson.Redirect;
            }
            return resultsJson;
        } else {
            return resultsText;
        }
    } else {
        // The delete was refused because other resources still reference the target. Surface it
        // globally (mirroring the Redirect branch above) so every delete call site reports it
        // without a code change, and also hand it back so a caller can handle it locally.
        if (response.status === 409 && resultsText) {
            try {
                const blocked = JSON.parse(resultsText);
                if (blocked && blocked.Code === DeleteBlockedCode) {
                    notifyDeleteBlocked(blocked);
                    return { status: 409, error: blocked.Message, blocked: blocked };
                }
            } catch (error) {
                // Not a blocked-delete body - fall through to the generic error shape.
            }
        }
        return {
            status: response.status,
            error: resultsText || "Something went wrong"
        };
    }
}

const useBaseResource = (defaultValue, initialUrl) => {
    const abortControllerRef = useRef(null);
    const urlRef = useRef(initialUrl);
    const [data, setData] = useState(defaultValue);
    const [error, setError] = useState(undefined);
    const [loading, setLoading] = useState(!!initialUrl);

    useEffect(() => {
        if (initialUrl) {
            initialize(initialUrl);
        }
        return () => {
            abortControllerRef.current?.abort();
        };
    }, []);

    const initialize = async (url) => {
        urlRef.current = url;
        abortControllerRef.current?.abort();
        const controller = new AbortController();
        abortControllerRef.current = controller;
        setLoading(true);
        const response = await data_get(url, controller.signal);
        if (controller.signal.aborted) return;
        if (response.error) {
            setError(response.error);
            setData(defaultValue);
        } else {
            setData(response);
            setError(undefined);
        }
        setLoading(false);
        return response;
    };

    const refresh = () => {
        if (urlRef.current) {
            return initialize(urlRef.current);
        }
    };

    return { data, setData, loading, error, initialize, refresh, urlRef };
};

/**
 * @template T
 * @typedef {Object} UseResourceResult
 * @property {T} data
 * @property {(value: T | ((current: T) => T)) => void} setData
 * @property {(fieldOrValue: keyof T | T, value?: any) => void} setField
 * @property {boolean} loading
 * @property {any} error
 * @property {(url: string) => Promise<T>} initialize
 * @property {() => Promise<T> | undefined} refresh
 * @property {(field: keyof T, value: any) => Promise<void>} update
 * @property {(field: keyof T, value: any) => Promise<void>} setAndUpdate
 * @property {() => Promise<any>} remove
 * @property {() => Promise<any>} canDelete
 */
/**
 * @template T
 * @param {T} defaultValue
 * @param {string} [initialUrl]
 * @param {{ resourceType?: string }} [options]
 * @returns {UseResourceResult<T>}
 */
export const useResource = (defaultValue, initialUrl, { resourceType } = {}) => {
    const { data, setData, loading, error, initialize, refresh, urlRef } = useBaseResource(defaultValue, initialUrl);

    const setField = (fieldOrValue, value) => {
        if (value !== undefined) {
            setData(current => ({ ...current, [fieldOrValue]: value }));
        } else {
            setData(fieldOrValue);
        }
    };

    const update = async (field, value) => {
        await data_patch(urlRef.current, { [field]: value });
    };

    const setAndUpdate = async (field, value) => {
        setField(field, value);
        await data_patch(urlRef.current, { [field]: value });
    };

    const remove = async () => {
        return await data_delete(urlRef.current);
    };

    const canDelete = async () => {
        const id = urlRef.current.split("/").pop();
        return await data_get(`/Usage/CanDelete/${id}?type=${resourceType}`);
    };

    return { data, setData, setField, loading, error, initialize, refresh, update, setAndUpdate, remove, canDelete, url: urlRef.current, resourceType };
};

/**
 * @template T
 * @typedef {Object} UseResourcesResult
 * @property {T[]} data
 * @property {(items: T[] | ((current: T[]) => T[])) => void} setData
 * @property {(id: any, field: keyof T, value: any) => void} setField
 * @property {boolean} loading
 * @property {any} error
 * @property {(url: string) => Promise<T[]>} initialize
 * @property {() => Promise<T[]> | undefined} refresh
 * @property {(id: any, field: keyof T, value: any) => Promise<void>} update
 * @property {(body: Partial<T>) => Promise<T>} add
 * @property {(id: any) => Promise<any>} remove
 * @property {(movedId: any, startIndex: number, endIndex: number) => Promise<void>} reorder
 * @property {(id: any) => Promise<any>} canDelete
 */
/**
 * @template T
 * @param {string} [initialUrl]
 * @param {{ mutationUrl?: string, idField?: string, resourceType?: string }} [options]
 * @returns {UseResourcesResult<T>}
 */
export const useResources = (initialUrl, { mutationUrl, idField, resourceType } = {}) => {
    const { data, setData, loading, error, initialize, refresh, urlRef } = useBaseResource([], initialUrl);

    const setItem = (id, field, value) => {
        setData(current => current.map(item =>
            item[idField] === id ? { ...item, [field]: value } : item
        ));
    };

    const update = async (id, field, value) => {
        await data_patch(`${mutationUrl}/${id}`, { [field]: value });
    };

    const add = async (body) => {
        const response = await data_post(mutationUrl, body);
        if (!response.error) {
            setData(current => [...current, response]);
        }
        return response;
    };

    const remove = async (id) => {
        const response = await data_delete(`${mutationUrl}/${id}`);
        // Only drop the row when the server actually deleted it. Previously the row disappeared even
        // on a refused delete and came back on the next refresh.
        if (!response?.error) {
            setData(current => current.filter(item => item[idField] !== id));
        }
        return response;
    };

    const reorder = async (movedId, startIndex, endIndex) => {
        setData(current => {
            const reordered = current.slice(0);
            reordered.splice(
                endIndex < 0 ? reordered.length + endIndex : endIndex,
                0,
                reordered.splice(startIndex, 1)[0]
            );
            return reordered;
        });
        await data_patch(`${mutationUrl}/${movedId}/reorder?endIndex=${endIndex}`);
    };

    const canDelete = async (id) => {
        return await data_get(`/Usage/CanDelete/${id}?type=${resourceType}`);
    };

    const [selectedIds, setSelectedIds] = useState(new Set());

    const toggleSelected = useCallback((id, value) => {
        setSelectedIds(prev => {
            const next = new Set(prev);
            if (value) next.add(id);
            else next.delete(id);
            return next;
        });
    }, []);

    const addManySelected = useCallback((ids) => {
        setSelectedIds(prev => {
            const next = new Set(prev);
            for (const id of ids) next.add(id);
            return next;
        });
    }, []);

    const removeManySelected = useCallback((ids) => {
        setSelectedIds(prev => {
            const next = new Set(prev);
            for (const id of ids) next.delete(id);
            return next;
        });
    }, []);

    const clearSelected = useCallback(() => {
        setSelectedIds(new Set());
    }, []);

    const selectAll = useCallback(() => {
        if (!idField) return;
        setSelectedIds(new Set(data.map(row => row[idField])));
    }, [data, idField]);

    const selectedData = idField
        ? data.filter(row => selectedIds.has(row[idField]))
        : [];

    const selection = {
        ids: selectedIds,
        count: selectedIds.size,
        isBulkMode: selectedIds.size > 0,
        selectedData,
        toggle: toggleSelected,
        addMany: addManySelected,
        removeMany: removeManySelected,
        selectAll,
        clear: clearSelected
    };

    return { data, setData, setField: setItem, loading, error, initialize, refresh, update, add, remove, reorder, canDelete, url: urlRef.current, resourceType, selection };
};

export function formatDateForPostBody(value) {
    if (!value) return null;
    const dateValue = new Date(value);
    if (!dateValue) return null;
    return dateValue.toISOString().split("T")[0];
}
